import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, Users, Globe, Sun, Moon, Star, Sparkles, Calendar, Clock, MapPin } from 'lucide-react';
import { Button } from './ui/Button';
interface HeroBannerProps {
  onNavigate: (page: string) => void;
}
export function HeroBanner({
  onNavigate
}: HeroBannerProps) {
  // Memoize star positions so they don't recalculate on every re-render
  const stars = useMemo(() => Array.from({
    length: 50
  }, (_, i) => ({
    id: i,
    width: Math.random() * 2 + 1,
    height: Math.random() * 2 + 1,
    top: Math.random() * 100,
    left: Math.random() * 100,
    opacity: Math.random() * 0.5 + 0.1,
    duration: Math.random() * 4 + 2,
    delay: Math.random() * 2
  })), []);
  return <section className="relative w-full min-h-screen flex flex-col overflow-hidden bg-[#050b14] font-sans pt-20 lg:pt-0" data-id="element-13499">
      {/* Cosmic Background */}
      <div className="absolute inset-0 pointer-events-none z-0" data-id="element-13500">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-[#112240] via-[#0a1628] to-[#050b14] opacity-90" data-id="element-13501" />

        {/* Stars */}
        {stars.map(s => <div key={s.id} className="absolute rounded-full bg-white" style={{
        width: `${s.width}px`,
        height: `${s.height}px`,
        top: `${s.top}%`,
        left: `${s.left}%`,
        opacity: s.opacity,
        animation: `pulse ${s.duration}s infinite ${s.delay}s`
      }} data-id="element-13502" />)}

        {/* Glowing Orbs */}
        <div className="absolute top-[10%] left-[20%] w-[500px] h-[500px] rounded-full bg-indigo-600/10 blur-[120px]" data-id="element-13503" />
        <div className="absolute bottom-[20%] right-[10%] w-[600px] h-[600px] rounded-full bg-amber-600/10 blur-[150px]" data-id="element-13504" />
        <div className="absolute top-[40%] right-[30%] w-[300px] h-[300px] rounded-full bg-blue-500/10 blur-[100px]" data-id="element-13505" />
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col lg:flex-row relative z-10 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 items-center pb-24 lg:pb-0" data-id="element-13506">
        {/* Left Column - Text & Form */}
        <div className="w-full lg:w-1/2 flex flex-col justify-center pt-10 lg:pt-0 z-20" data-id="element-13507">
          {/* Glassmorphism Form */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} className="bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-2xl p-5 mb-10 shadow-[0_8px_32px_rgba(0,0,0,0.3)] flex flex-col sm:flex-row gap-4 sm:gap-6 w-full max-w-2xl" data-id="element-13508">
            <div className="flex-1" data-id="element-13509">
              <label className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2" data-id="element-13510">
                <Calendar className="w-3 h-3" data-id="element-13511" /> Дата рождения
              </label>
              <input type="text" placeholder="напр., 26 Окт 1990" className="w-full bg-transparent border-b border-white/20 pb-2 text-white text-sm focus:outline-none focus:border-indigo-400 transition-colors placeholder:text-slate-600" data-id="element-13512" />
            </div>
            <div className="flex-1" data-id="element-13513">
              <label className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2" data-id="element-13514">
                <Clock className="w-3 h-3" data-id="element-13515" /> Время рождения
              </label>
              <input type="text" placeholder="напр., 14:15" className="w-full bg-transparent border-b border-white/20 pb-2 text-white text-sm focus:outline-none focus:border-indigo-400 transition-colors placeholder:text-slate-600" data-id="element-13516" />
            </div>
            <div className="flex-1" data-id="element-13517">
              <label className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2" data-id="element-13518">
                <MapPin className="w-3 h-3" data-id="element-13519" /> Место рождения
              </label>
              <input type="text" placeholder="Город, Страна" className="w-full bg-transparent border-b border-white/20 pb-2 text-white text-sm focus:outline-none focus:border-indigo-400 transition-colors placeholder:text-slate-600" data-id="element-13520" />
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2,
          ease: 'easeOut'
        }} data-id="element-13521">
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-white via-indigo-100 to-slate-300 mb-6 tracking-tight" data-id="element-13522">
              РАСКРОЙТЕ СВОЙ
              <br data-id="element-13523" />
              КОСМИЧЕСКИЙ
              <br data-id="element-13524" />
              ПОТЕНЦИАЛ
            </h1>

            <p className="text-sm sm:text-lg text-slate-300/80 mb-10 max-w-lg font-light" data-id="element-13525">
              Поймите свое жизненное предназначение с помощью
              персонализированных натальных карт, анализа транзитов и
              рекомендаций экспертов. Создайте свою карту мгновенно.
            </p>

            <div className="flex flex-col sm:flex-row gap-4" data-id="element-13526">
              <Button size="lg" className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-400 hover:to-purple-500 text-white border-none rounded-full px-8 py-6 text-sm font-bold tracking-wider uppercase shadow-[0_0_30px_rgba(99,102,241,0.3)] transition-all hover:shadow-[0_0_40px_rgba(99,102,241,0.5)]" onClick={() => onNavigate('service-natal')} data-id="element-13527">
                Рассчитать карту
              </Button>
              <Button size="lg" variant="outline" className="bg-white/5 hover:bg-white/10 text-white border-white/20 rounded-full px-8 py-6 text-sm font-bold tracking-wider uppercase backdrop-blur-sm transition-all" onClick={() => onNavigate('natal-demo')} data-id="element-13528">
                Смотреть пример
              </Button>
            </div>
          </motion.div>
        </div>

        {/* Right Column - Visual Chart */}
        <div className="w-full lg:w-1/2 relative flex items-center justify-center min-h-[400px] sm:min-h-[500px] lg:min-h-[600px] mt-16 lg:mt-0 z-10" data-id="element-13529">
          <div className="relative w-[320px] h-[320px] sm:w-[450px] sm:h-[450px] lg:w-[600px] lg:h-[600px] flex items-center justify-center" data-id="element-13530">
            {/* Outer Glow */}
            <div className="absolute inset-0 rounded-full bg-indigo-500/5 blur-[100px]" data-id="element-13531" />

            {/* Ring 1 (Outer) */}
            <motion.div className="absolute inset-[5%] rounded-full border border-white/10 border-dashed" animate={{
            rotate: 360
          }} transition={{
            duration: 120,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-13532">
              {/* Planet: Sun */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center" data-id="element-13533">
                <div className="w-8 h-8 rounded-full bg-[#0a1628] border border-amber-500/50 flex items-center justify-center shadow-[0_0_15px_rgba(245,158,11,0.4)]" data-id="element-13534">
                  <Sun className="w-4 h-4 text-amber-400" data-id="element-13535" />
                </div>
                <motion.div className="absolute top-10 whitespace-nowrap bg-[#0a1628]/80 backdrop-blur-sm border border-white/10 px-3 py-1.5 rounded-full text-[10px] text-slate-300 font-medium tracking-wide" animate={{
                rotate: -360
              }} transition={{
                duration: 120,
                repeat: Infinity,
                ease: 'linear'
              }} data-id="element-13536">
                  Солнце в Скорпионе
                </motion.div>
              </div>

              {/* Planet: Ascendant */}
              <div className="absolute bottom-[15%] right-[5%] translate-x-1/2 translate-y-1/2 flex flex-col items-center" data-id="element-13537">
                <div className="w-8 h-8 rounded-full bg-[#0a1628] border border-emerald-500/50 flex items-center justify-center shadow-[0_0_15px_rgba(16,185,129,0.4)]" data-id="element-13538">
                  <Star className="w-4 h-4 text-emerald-400" data-id="element-13539" />
                </div>
                <motion.div className="absolute top-10 whitespace-nowrap bg-[#0a1628]/80 backdrop-blur-sm border border-white/10 px-3 py-1.5 rounded-full text-[10px] text-slate-300 font-medium tracking-wide" animate={{
                rotate: -360
              }} transition={{
                duration: 120,
                repeat: Infinity,
                ease: 'linear'
              }} data-id="element-13540">
                  Асцендент во Льве
                </motion.div>
              </div>
            </motion.div>

            {/* Ring 2 (Middle) */}
            <motion.div className="absolute inset-[20%] rounded-full border border-indigo-500/20" animate={{
            rotate: -360
          }} transition={{
            duration: 90,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-13541">
              {/* Zodiac signs markers (decorative) */}
              {[...Array(12)].map((_, i) => {
              const angle = i * 30 * (Math.PI / 180);
              const radius = 50;
              const x = 50 + radius * Math.cos(angle - Math.PI / 2);
              const y = 50 + radius * Math.sin(angle - Math.PI / 2);
              return <div key={i} className="absolute w-1.5 h-1.5 rounded-full bg-indigo-400/60" style={{
                top: `${y}%`,
                left: `${x}%`,
                transform: 'translate(-50%, -50%)',
                boxShadow: '0 0 6px rgba(129,140,248,0.4)'
              }} data-id="element-13542" />;
            })}

              {/* Planet: Moon */}
              <div className="absolute top-[20%] right-[5%] translate-x-1/2 -translate-y-1/2 flex flex-col items-center" data-id="element-13543">
                <div className="w-8 h-8 rounded-full bg-[#0a1628] border border-blue-400/50 flex items-center justify-center shadow-[0_0_15px_rgba(96,165,250,0.4)]" data-id="element-13544">
                  <Moon className="w-4 h-4 text-blue-300" data-id="element-13545" />
                </div>
                <motion.div className="absolute top-10 whitespace-nowrap bg-[#0a1628]/80 backdrop-blur-sm border border-white/10 px-3 py-1.5 rounded-full text-[10px] text-slate-300 font-medium tracking-wide" animate={{
                rotate: 360
              }} transition={{
                duration: 90,
                repeat: Infinity,
                ease: 'linear'
              }} data-id="element-13546">
                  Луна в Водолее
                </motion.div>
              </div>
            </motion.div>

            {/* Ring 3 (Inner) */}
            <motion.div className="absolute inset-[40%] rounded-full border border-white/10 border-dotted" animate={{
            rotate: 360
          }} transition={{
            duration: 60,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-13547">
              {/* Inner geometric lines */}
              <svg className="absolute inset-0 w-full h-full opacity-20" viewBox="0 0 100 100" data-id="element-13548">
                <polygon points="50,0 100,75 0,75" fill="none" stroke="#818cf8" strokeWidth="0.5" data-id="element-13549" />
                <polygon points="50,100 100,25 0,25" fill="none" stroke="#818cf8" strokeWidth="0.5" data-id="element-13550" />
                <line x1="50" y1="0" x2="50" y2="100" stroke="#818cf8" strokeWidth="0.5" data-id="element-13551" />
                <line x1="0" y1="50" x2="100" y2="50" stroke="#818cf8" strokeWidth="0.5" data-id="element-13552" />
              </svg>
            </motion.div>

            {/* Center Core */}
            <div className="absolute inset-[45%] rounded-full bg-gradient-to-tr from-indigo-900/40 to-purple-900/40 border border-indigo-500/30 backdrop-blur-md flex items-center justify-center shadow-[0_0_50px_rgba(99,102,241,0.2)]" data-id="element-13553">
              <Sparkles className="w-8 h-8 text-indigo-300 opacity-80" data-id="element-13554" />
              <motion.div className="absolute inset-0 rounded-full border border-indigo-400/50" animate={{
              scale: [1, 1.2, 1],
              opacity: [0.5, 0, 0.5]
            }} transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-13555" />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Statistics Strip */}
      <div className="relative z-20 w-full bg-[#050b14]/80 backdrop-blur-xl border-t border-white/10 py-6 mt-auto" data-id="element-13556">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-13557">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-4 divide-y sm:divide-y-0 sm:divide-x divide-white/10" data-id="element-13558">
            {/* Stat 1 */}
            <div className="flex items-center justify-center gap-4 pt-4 sm:pt-0" data-id="element-13559">
              <div className="w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center" data-id="element-13560">
                <BarChart3 className="w-5 h-5 text-amber-400" data-id="element-13561" />
              </div>
              <div data-id="element-13562">
                <div className="text-2xl sm:text-3xl font-bold text-white tracking-tight" data-id="element-13563">
                  23,000+
                </div>
                <div className="text-[10px] sm:text-xs text-slate-400 uppercase tracking-widest font-semibold mt-1" data-id="element-13564">
                  Рассчитанных карт
                </div>
              </div>
            </div>

            {/* Stat 2 */}
            <div className="flex items-center justify-center gap-4 pt-4 sm:pt-0" data-id="element-13565">
              <div className="w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center" data-id="element-13566">
                <Users className="w-5 h-5 text-indigo-400" data-id="element-13567" />
              </div>
              <div data-id="element-13568">
                <div className="text-2xl sm:text-3xl font-bold text-white tracking-tight" data-id="element-13569">
                  120+
                </div>
                <div className="text-[10px] sm:text-xs text-slate-400 uppercase tracking-widest font-semibold mt-1" data-id="element-13570">
                  Проф. астрологов
                </div>
              </div>
            </div>

            {/* Stat 3 */}
            <div className="flex items-center justify-center gap-4 pt-4 sm:pt-0" data-id="element-13571">
              <div className="w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center" data-id="element-13572">
                <Globe className="w-5 h-5 text-emerald-400" data-id="element-13573" />
              </div>
              <div data-id="element-13574">
                <div className="text-2xl sm:text-3xl font-bold text-white tracking-tight" data-id="element-13575">
                  54
                </div>
                <div className="text-[10px] sm:text-xs text-slate-400 uppercase tracking-widest font-semibold mt-1" data-id="element-13576">
                  Страны мира
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}